/**
 * Spinner — indicateur de chargement circulaire.
 *
 * @remarks
 * Anneau CSS (pas d'icône) qui tourne via `animate-spin`. La couleur est
 * héritée de `currentColor` : posé dans un bouton `text-white` il est blanc,
 * ailleurs passer `text-primary` / `text-fg-muted` via `className`.
 *
 * Extrait pour dédupliquer les ~7 copies verbatim de ce `<span>` dans les
 * boutons submit (auth/profil). Les loaders pleine-page riches à base de
 * `Loader2` (lucide) côté features ne sont volontairement pas couverts ici —
 * c'est un autre besoin visuel.
 *
 * **A11y** : décoratif par défaut (`aria-hidden`) puisqu'il accompagne
 * presque toujours un texte d'état. Passer `label` quand le spinner est le
 * seul indicateur de chargement → expose un `role="status"`.
 */

import { cn } from '@/lib/utils/cn';

type SpinnerSize = 'sm' | 'md' | 'lg';

export interface SpinnerProps {
  size?: SpinnerSize;
  className?: string;
  /** Libellé lecteur d'écran. Fourni → `role="status"` ; absent → décoratif. */
  label?: string;
}

const SIZES: Record<SpinnerSize, string> = {
  sm: 'size-3.5 border-[1.5px]',
  md: 'size-5 border-2',
  lg: 'size-8 border-[3px]',
};

export function Spinner({ size = 'sm', className, label }: SpinnerProps) {
  const a11y =
    label !== undefined ? { role: 'status', 'aria-label': label } : { 'aria-hidden': true };

  return (
    <span
      {...a11y}
      className={cn(
        'inline-block animate-spin rounded-full border-current/30 border-t-current',
        SIZES[size],
        className,
      )}
    />
  );
}
